/**
 * Per-user UI preferences persisted in localStorage.
 *
 * Follows the same shape as saveThemePreference/loadThemePreference in ./themeSelector.js:
 * try/catch around every localStorage access (it throws in private-browsing and quota cases),
 * logging on failure, and a safe default on read.
 *
 * These are UI nudges only - never store sensitive or tenant data here.
 */

import logger from './logger';

const IMPACT_PROMPT_KEY = 'promptImpactAssessment';

/**
 * Save whether the post-save "Run Impact Assessment?" prompt should appear.
 *
 * Deliberately NOT tenant-scoped: the preference belongs to the person and their current task
 * ("don't interrupt me while I key a batch of rates"), not to a tenant's data. Scoping it per tenant
 * would restore the prompt mid-flow when an operator switches tenant to continue the same load, and
 * would accumulate one orphan key per tenant forever.
 *
 * @param {boolean} enabled - true to prompt after saving an interest rate
 */
export const saveImpactPromptPreference = (enabled) => {
  try {
    localStorage.setItem(IMPACT_PROMPT_KEY, enabled ? 'true' : 'false');
  } catch (error) {
    logger.error('Failed to save impact prompt preference', { message: error.message });
  }
};

/**
 * Load the impact-assessment prompt preference.
 * Defaults to true (prompt shown) when unset or unreadable, preserving the original behaviour.
 *
 * @returns {boolean} true if the prompt should appear
 */
export const loadImpactPromptPreference = () => {
  try {
    return localStorage.getItem(IMPACT_PROMPT_KEY) !== 'false';
  } catch (error) {
    logger.error('Failed to load impact prompt preference', { message: error.message });
    return true;
  }
};
